import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { toast } from "react-toastify";
import ConfirmDialog from "src/Common/confirmdialog";
import { formatDate } from "src/Common/common";
import { isAdmin } from "src/Common/function";
import DropDown from "src/Components/EditDeleteDropdown/dropdown";
import ServiceTypeService from "src/Services/API/service.service";
import UserService from "src/Services/API/user.service";
import ServiceTypeAddModal from "./ServiceTypeAddModal";

const Active = forwardRef((props, ref) => {
  const currentUser = UserService.getCurrentUser();
  const [services, setServices] = useState([]);
  const [isAdd, setIsAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isArchiveConfirmDialogShown, setIsArchiveConfirmDialogShown] = useState(false);
  const [indexForArchive, setIndexForArchive] = useState(null);

  useEffect(() => {
    getServices();
  }, []);

  const getServices = async () => {
    try {
      const response = await ServiceTypeService.getAll();
      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : [];
        const activeServices = data.filter((service) => service?.isActive !== false && service?.isArchived !== true);
        setServices(activeServices);
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      console.log("Service type load error: ", error);
      toast(error?.response?.data?.message || "Failed to load services");
    }
  };

  const openAddModal = () => {
    setIsAdd(true);
    setIsEdit(false);
    setEditingService(null);
  };

  useImperativeHandle(ref, () => ({ openAddModal }));

  const openEditModal = (index) => {
    setIsEdit(true);
    setIsAdd(false);
    setEditingService(services[index]);
  };

  const closeModal = () => {
    setIsAdd(false);
    setIsEdit(false);
    setEditingService(null);
  };

  const handleSave = async (serviceType) => {
    const isUpdating = Boolean(editingService);
    const payload = isUpdating ? { ...editingService, serviceType } : { serviceType, company: currentUser.company };
    console.log("🚀 ~ handleSave ~ payload:", payload);

    try {
      const response = isUpdating ? await ServiceTypeService.update(payload) : await ServiceTypeService.save(payload);
      if (response.status === 200 || response.status === 201) {
        toast(isUpdating ? "Service updated successfully" : "Service added successfully");
        getServices();
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      console.log("Service type save error: ", error);
      toast(error?.response?.data?.message || "Failed to save service");
    } finally {
      closeModal();
    }
  };

  const requestArchive = (index) => {
    setIndexForArchive(index);
    setIsArchiveConfirmDialogShown(true);
  };

  const archiveService = async () => {
    const target = services[indexForArchive];
    if (!target?.id) {
      setIsArchiveConfirmDialogShown(false);
      return;
    }
    try {
      const response = await ServiceTypeService.archive(target.id);
      if (response.status === 200 || response.status === 201) {
        toast("Service archived successfully");
        getServices();
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      console.log("Service type archive error: ", error);
      toast(error?.response?.data?.message || "Failed to archive service");
    } finally {
      setIsArchiveConfirmDialogShown(false);
      setIndexForArchive(null);
    }
  };

  return (
    <div>
      {isAdd && <ServiceTypeAddModal onSave={handleSave} onCancel={closeModal} serviceType={null} />}
      {isEdit && <ServiceTypeAddModal onSave={handleSave} onCancel={closeModal} serviceType={editingService} />}

      {isArchiveConfirmDialogShown && (
        <ConfirmDialog
          title="ARCHIVE SERVICE"
          description="Are you sure you want to archive this service?"
          shown={isArchiveConfirmDialogShown}
          onConfirmClicked={archiveService}
          onCancelClicked={() => setIsArchiveConfirmDialogShown(false)}
        />
      )}

      <div
        className="w-full flex flex-col overflow-hidden max-h-[calc(100vh-240px)]"
        style={{
          borderRadius: "12px",
          border: "1px solid #E5E8F3",
          background: "#FFF",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr
                style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}
                className="[&>th]:border-r [&>th]:border-borderColor [&>th:last-child]:border-r-0"
              >
                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    <span>Name</span>
                  </div>
                </th>
                <th className="w-[180px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 h-[44px] text-[#5F6477]">
                    Date Added
                  </div>
                </th>
                <th className="w-[48px]"></th>
              </tr>
            </thead>
            <tbody>
              {services.map((service, i) => (
                <tr
                  key={service.id || service.name || i}
                  className="border-b border-borderColor hover:bg-[#FAFAFB] [&>td]:border-r [&>td]:border-borderColor [&>td:last-child]:border-r-0"
                >
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] col-span-1">
                      {service.serviceType}
                    </div>
                  </td>

                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] col-span-1">
                      {service.createdAt ? formatDate(service.createdAt) : "-"}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center justify-center px-2 py-3">
                      {isAdmin() && (
                        <DropDown index={i} onDeleteClicked={requestArchive} onEditClicked={openEditModal} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {services.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-10 text-[12px] font-OpenSansRegular text-subTitleColor">
                    No services yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

export default Active;
